import { createSlice, nanoid, PayloadAction } from '@reduxjs/toolkit'
import { UploadFile } from 'antd/lib/upload/interface'
import { Action, Middleware } from 'redux'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { batch } from 'react-redux'
import appAPI from '../../api/api'
import { ForumCommentRead } from '../../api/forum-comment'
import { FileDeliveryProgress } from '../../api/forum-upload'
import { outboxStorage } from '../forum-outbox-storage'
import { transferForumFile } from '../forum-file-transfer'
import { RootState } from '../store'
import { clearUserData, fetchLogOut } from './accountSlice'
import { forumCommentsActions } from './commentSlice'
import { forumTopicActions } from './topicSlice'

export type CommentDraft = {
  message: string
  files: UploadFile[]
  removals: string[]
}

export type OutboxMessage = CommentDraft & {
  key: string
  topicId: number
  userId?: number
  comment: ForumCommentRead
  editing: boolean
  serverId?: number
  completed: string[]
  retryAt?: number
  ready?: boolean
  queuedAt?: number
  progress?: Record<string, FileDeliveryProgress>
  error?: string
  recoverable?: boolean
  status: 'queued' | 'sending' | 'failed'
}

const slice = createSlice({
  name: 'forumOutbox',
  initialState: { messages: [] as OutboxMessage[], nextId: -1, restoreError: undefined as string | undefined },
  reducers: {
    add: (state, { payload }: PayloadAction<OutboxMessage>) => {
      state.messages.push(payload)
      state.nextId -= 1
    },
    change: (state, { payload }: PayloadAction<{ key: string; changes: Partial<OutboxMessage> }>) => {
      const job = state.messages.find((item) => item.key === payload.key)
      if (job) Object.assign(job, payload.changes)
    },
    remove: (state, { payload }: PayloadAction<string>) => {
      state.messages = state.messages.filter((item) => item.key !== payload)
    },
    restore: (state, { payload }: PayloadAction<OutboxMessage[]>) => {
      for (const job of payload) {
        if (state.messages.some((item) => item.key === job.key)) continue
        const resumed = { ...job, ready: true, retryAt: undefined }
        if (resumed.status !== 'failed' || resumed.recoverable) resumed.status = 'queued'
        if (resumed.comment.id < 0) resumed.comment = { ...resumed.comment, id: state.nextId-- }
        state.messages.push(resumed)
      }
      state.messages.sort((a, b) => (a.queuedAt || 0) - (b.queuedAt || 0))
      state.restoreError = undefined
    },
    restoreFailed: (state) => {
      state.restoreError = 'Не удалось восстановить очередь файлов. Проверьте доступность хранилища браузера.'
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearUserData.pending, (state) => {
      state.messages = []
      state.restoreError = undefined
    })
    builder.addCase(fetchLogOut.pending, (state) => {
      state.messages = []
      state.restoreError = undefined
    })
  },
})

type OutboxThunk = ThunkAction<void, RootState, unknown, Action>
const hasFiles = (job: OutboxMessage) => job.files.some((file) => !!file.originFileObj) || job.removals.length > 0

export const retryAfterMilliseconds = (value?: string | number) => {
  const seconds = Number(value)
  const delay = Number.isFinite(seconds) ? seconds * 1000 : Date.parse(String(value)) - Date.now()
  return Number.isFinite(delay) ? Math.max(1000, Math.min(delay, 2147483647)) : 1000
}

const drain = (): OutboxThunk => async (dispatch, getState) => {
  const messages = getState().forumOutbox.messages
  if (messages.some((item) => item.status === 'sending' && (!item.completed.includes('message') || item.retryAt)))
    return
  const firstWrite = messages.find((item) => item.status === 'queued' && !item.completed.includes('message'))
  if (firstWrite?.ready === false) return
  const fileWorkerBusy = messages.some((item) => item.status === 'sending' && hasFiles(item))
  const job = messages.find(
    (item) =>
      item.status === 'queued' &&
      item.ready !== false &&
      (!hasFiles(item) || !item.completed.includes('message') || !fileWorkerBusy),
  )
  if (!job) return
  const { topicId } = job
  const current = () =>
    getState().account.user?.id === job.userId && getState().forumOutbox.messages.some((item) => item.key === job.key)
  if (!current()) {
    dispatch(slice.actions.remove(job.key))
    dispatch(drain())
    return
  }
  dispatch(slice.actions.change({ key: job.key, changes: { status: 'sending', retryAt: undefined } }))
  let serverId = job.serverId
  const completed = [...job.completed]
  const checkpoint = async (changes: Partial<OutboxMessage>) => {
    if (!current()) return
    dispatch(slice.actions.change({ key: job.key, changes }))
    const latest = getState().forumOutbox.messages.find((item) => item.key === job.key)
    if (latest && hasFiles(latest)) await outboxStorage.checkpoint(latest)
  }
  try {
    if (hasFiles(job)) await checkpoint({ error: undefined })
    if (!current()) return
    let comment: ForumCommentRead
    if (serverId && completed.includes('message')) {
      comment = job.comment
    } else {
      const response = job.editing
        ? await appAPI.forumComments.putComment({
            id: serverId!,
            message: job.message,
            ...(hasFiles(job) ? { attachmentsChanged: true } : {}),
          })
        : await appAPI.forumComments.postComment({
            topic: topicId,
            parent: job.comment.parent?.id,
            message: job.message,
            clientId: job.key,
          })
      comment = response.data
      serverId = comment.id
      completed.push('message')
      if (!current()) return
      if (hasFiles(job)) await checkpoint({ serverId, comment, completed: [...completed] })
    }
    if (
      hasFiles(job) &&
      getState().forumOutbox.messages.some(
        (item) => item.key !== job.key && item.status === 'sending' && hasFiles(item),
      )
    ) {
      await checkpoint({ status: 'queued' })
      return
    }
    // Message writes stay ordered, but a file transfer no longer blocks later text.
    if (hasFiles(job)) dispatch(drain())
    const record = async (key: string) => {
      completed.push(key)
      await checkpoint({ completed: [...completed] })
    }
    for (const file of job.files) {
      if (!current()) return
      const key = `upload:${file.uid}`
      if (!file.originFileObj || completed.includes(key)) continue
      await transferForumFile(serverId!, `${job.key}:${file.uid}`, file, current, (progress) => {
        const active = getState().forumOutbox.messages.find((item) => item.key === job.key)
        if (active)
          dispatch(
            slice.actions.change({ key: job.key, changes: { progress: { ...active.progress, [file.uid]: progress } } }),
          )
      })
      if (!current()) return
      await record(key)
    }
    for (const id of job.removals) {
      if (!current()) return
      const key = `remove:${id}`
      if (completed.includes(key)) continue
      try {
        await appAPI.forumComments.deleteAttachment(Number(id))
      } catch (error: any) {
        if (error.response?.status !== 404) throw error
      }
      await record(key)
    }
    if (hasFiles(job)) {
      comment = (await appAPI.forumComments.getComment(serverId!)).data
      // Delete the durable job before removing the visible outgoing row.
      // A lost delete only replays idempotent message/upload operations.
      await outboxStorage.remove(job.key)
    }
    if (!current()) return
    batch(() => {
      if (getState().forumComments.topicId === topicId) {
        dispatch(
          job.editing
            ? forumCommentsActions.applyRealtimeCommentUpdated(comment)
            : forumCommentsActions.applyRealtimeCommentCreated(comment),
        )
        dispatch(forumTopicActions.setPinnedCommentFromMessage(comment))
      }
      dispatch(slice.actions.remove(job.key))
    })
  } catch (error: any) {
    if (current() && error.response?.status === 429) {
      const delay = retryAfterMilliseconds(error.response.headers?.['retry-after'])
      dispatch(slice.actions.change({ key: job.key, changes: { retryAt: Date.now() + delay } }))
      await new Promise((resolve) => setTimeout(resolve, delay))
      if (current()) {
        dispatch(slice.actions.change({ key: job.key, changes: { status: 'queued', retryAt: undefined } }))
      }
    } else if (current()) {
      const changes: Partial<OutboxMessage> = {
        status: 'failed',
        error: error.message || 'Не удалось отправить файл.',
        recoverable: (!error.response || error.response.status >= 500) && !error.permanent,
      }
      dispatch(slice.actions.change({ key: job.key, changes }))
      if (hasFiles(job)) {
        try {
          await checkpoint(changes)
        } catch (_) {
          /* Keep the in-memory draft available for retry. */
        }
      }
    }
  } finally {
    if (!current()) dispatch(slice.actions.remove(job.key))
    dispatch(drain())
  }
}

const prepareFiles =
  (key: string): OutboxThunk =>
  async (dispatch, getState) => {
    const job = getState().forumOutbox.messages.find((item) => item.key === key)
    if (!job) return
    try {
      await outboxStorage.save(job)
      if (
        getState().account.user?.id !== job.userId ||
        !getState().forumOutbox.messages.some((item) => item.key === key)
      ) {
        return
      }
      dispatch(slice.actions.change({ key, changes: { ready: true, status: 'queued', error: undefined } }))
      dispatch(drain())
    } catch (_) {
      dispatch(
        slice.actions.change({
          key,
          changes: {
            status: 'failed',
            ready: false,
            error: 'Не удалось сохранить файл в браузере. Освободите место и нажмите «Повторить».',
          },
        }),
      )
    }
  }

export const enqueueComment =
  (topicId: number, draft: CommentDraft): OutboxThunk =>
  (dispatch, getState) => {
    const state = getState()
    const user = state.account.user
    const editing = state.forumComments.editingComment
    const parent = state.forumComments.repliedToComment
    const key = nanoid()
    dispatch(
      slice.actions.add({
        ...draft,
        key,
        topicId,
        userId: user?.id,
        editing: !!editing,
        serverId: editing?.id,
        completed: [],
        status: 'queued',
        ready: !draft.files.some((file) => !!file.originFileObj) && !draft.removals.length,
        queuedAt: Date.now(),
        comment: {
          ...(editing || {}),
          id: editing?.id || state.forumOutbox.nextId,
          clientId: key,
          user:
            editing?.user ||
            (user
              ? {
                  id: user.id,
                  authorName:
                    [user.lastName, user.firstName, user.middleName].filter(Boolean).join(' ') || user.username,
                  avatar: user.avatar,
                }
              : null),
          message: draft.message,
          created: editing?.created || new Date().toISOString(),
          parent: editing?.parent || parent,
          attachments: editing?.attachments || [],
          isEdited: !!editing,
        } as ForumCommentRead,
      }),
    )
    if (draft.files.some((file) => !!file.originFileObj) || draft.removals.length) dispatch(prepareFiles(key))
    else dispatch(drain())
  }

export const retryComment =
  (key: string): OutboxThunk =>
  (dispatch, getState) => {
    const job = getState().forumOutbox.messages.find((item) => item.key === key)
    if (!job || job.status !== 'failed') return
    if (job.ready === false) {
      dispatch(prepareFiles(key))
      return
    }
    dispatch(slice.actions.change({ key, changes: { status: 'queued' } }))
    dispatch(drain())
  }

export const restoreForumOutbox =
  (userId: number): OutboxThunk =>
  async (dispatch, getState) => {
    try {
      const jobs = await outboxStorage.load(userId)
      if (getState().account.user?.id !== userId) return
      dispatch(slice.actions.restore(jobs))
      dispatch(drain())
    } catch (_) {
      if (getState().account.user?.id === userId) dispatch(slice.actions.restoreFailed())
    }
  }

export const forumOutboxMiddleware: Middleware = (store) => (next) => (action) => {
  const previousUser = store.getState().account.user?.id
  const logout = fetchLogOut.pending.match(action)
  const result = next(action)
  if (logout && previousUser !== undefined) outboxStorage.clearUser(previousUser).catch(() => undefined)
  const userId = store.getState().account.user?.id
  if (userId !== undefined && userId !== previousUser)
    (store.dispatch as ThunkDispatch<RootState, unknown, Action>)(restoreForumOutbox(userId))
  return result
}

export default slice.reducer
